import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { PatronLookupService } from './patron-lookup.service';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { SharedService } from '../../services/shared/shared.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { AuthService, LoginRequest } from '../../services/auth/auth.service';
import { Library } from '../../core/interfaces/library.interface';

@Component({
  selector: 'app-patron-lookup',
  templateUrl: './patron-lookup.component.html',
  styleUrls: ['./patron-lookup.component.scss'],
  providers: [{ provide: ANIMATION_MODULE_TYPE, useValue: 'BrowserAnimations' }],
})
export class PatronLookupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('userField') userField: ElementRef;
  login: UntypedFormGroup;
  filterLibrary: UntypedFormControl = new UntypedFormControl();
  cid: any;
  showLookup: boolean;
  showUnavailMessage: boolean;
  loading: boolean;
  libraries: Library[] = [];
  LIB: string;
  btnLoading = false;

  private _filteredLibs = new BehaviorSubject<Library[]>(null);
  private _userData: any;
  private _lid: any;
  private _libs: Library[] = [];
  private _libNames: any[] = [];
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  instructionalText1 = 'Please select your library and enter your username and password.';

  libraryDropText1 = 'Type your library name or library code';
  libraryDropText2 = 'Staff library';

  userNameText1 = 'User Name';
  userNameText2 = 'Staff login';
  passwordText1 = 'Password';
  passwordText2 = 'Staff password';

  constructor(
    private _authService: AuthService,
    private _fb: UntypedFormBuilder,
    private _patronLookupService: PatronLookupService,
    private _sharedService: SharedService,
    private _router: Router,
  ) {
    this.showLookup = false;
    this.loading = true;
    this.showUnavailMessage = false;
  }

  ngOnInit() {
    // START THE PATRON LOOPUP TOOL BY LOADING THE URL
    //eg url:- /PatronLookupTool?cid=stwi&lid=stwi

    this._sharedService.userData$.pipe(takeUntil(this._unsubscribeAll)).subscribe((userData) => {
      if (!userData) {
        return;
      }

      this.login = this._fb.group(
        {
          user: [''],
          password: [''],
          remember: [false],
        },
        { updateOn: 'blur' },
      );

      this._userData = userData; // check if this.userData is actually needed
      this.cid = userData.customerId?.toLowerCase();
      this._lid = userData.libraryId?.toLowerCase();
      this.showLookup = userData.patronLookupToolInfo.showPatronLookupTool;
      this.loading = false;
      if (this.showLookup) {
        this._getLibraries(this.cid);
        this.showUnavailMessage = false;
      } else {
        this.showUnavailMessage = true;
      }

      this.filterLibrary.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
        // listen for search field value changes
        this.filterLibs(value);
      });
      this._filteredLibs.subscribe((value) => {
        // listen to change the library list on search filter.
        this.libraries = value;
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.showLookup) {
      this.userField.nativeElement.focus();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loginSubmit(): void {
    this.btnLoading = true;
    const username = this.login.value.user;
    const password = this.login.value.password;
    let remember = this.login.value.remember;
    if (!remember) {
      remember = true;
    }

    // TODO: call central login here
    this._authService.login(this._buildGuestLoginData(username, password, remember)).subscribe(
      (userData) => {
        if (!userData.isStaff) {
          this.unAuthAcess();
          return;
        }

        this.showLookup = this._userData.patronLookupToolInfo.showPatronLookupTool;
        this.loading = false;
        if (this.showLookup) {
          this.btnLoading = false;
          this.showUnavailMessage = false;
          this._router.navigate(['/PatronLookupTool/info', this.cid, this._lid]);
        } else {
          this.showUnavailMessage = true;
        }
      },
      (error: unknown) => {
        this.showUnavailMessage = false;
        this.btnLoading = false;
        if (error instanceof Error) {
          this._sharedService.openSnackBar(error.message, 'ok');
        }
      },
    );
  }

  unAuthAcess(): void {
    this.btnLoading = false;
    const message = 'Only staff members can login';
    this._sharedService.openSnackBar(message, 'ok');
  }

  go2Home(): void {
    this._router.navigate(['/'], { queryParams: { cid: this.cid, lid: this._lid } });
  }

  filterLibs(value): void {
    const dummyArray: any = [];
    for (let index = 0; index < this._libs.length; index++) {
      const element = this._libs[index];
      if (element.lid.toUpperCase().includes(value.toUpperCase())) {
        dummyArray.push(element);
      } else if (element.name.toUpperCase().includes(value.toUpperCase())) {
        dummyArray.push(element);
      }
    }
    this._filteredLibs.next(dummyArray);

    let keepGoing2 = true;
    const dummyArray2 = [];
    const LIBRARIES = value;
    if (value) {
      if (value.includes(':')) {
        for (let index = 0; index < LIBRARIES.length; index++) {
          if (LIBRARIES.charAt(index) == ':') {
            keepGoing2 = false;
          }
          if (keepGoing2) {
            dummyArray2.push(LIBRARIES.charAt(index));
          }
        }
        const newLIB2 = dummyArray2.join('');
        this._lid = newLIB2;
      }
    }
  }

  showLibList(): void {
    this.LIB = '';
  }

  private _getLibraries(cid): void {
    this._patronLookupService
      .getLibraries(cid)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        (response) => {
          if (response.status == '200 OK') {
            this._libs = response.data;
            this.libraries = response.data;
            for (const lib of this._libs) {
              if (lib.lid.toUpperCase() == this._lid.toUpperCase()) {
                const id = lib.lid;
                this.LIB = id + ':' + lib.name;
                this.loading = false;
                this._libNames = [];
                for (let index = 0; index < this._libs.length; index++) {
                  const element = this._libs[index];
                  this._libNames.push(element.name);
                }
              }
            }
            if (this.showLookup) {
              setTimeout(() => {
                this.userField.nativeElement.focus();
              }, 2000);
            }
          }
        },
        (error: unknown) => {
          console.warn('get libraries guest patron lookup tool error');
          console.log(error);
        },
      );
  }

  private _buildGuestLoginData(user, pass, remember): LoginRequest {
    const result: LoginRequest = {
      grant_Type: 'password',
      cid: this.cid,
      lid: this._lid,
      password: pass,
      rememberMe: remember,
      userName: user,
    };
    return result;
  }
}
